// Single source of truth for sidebar-row sizing, typography, and hover/active
// states. Every row in the sidebar (New chat, Projects header, individual
// project, chat inside a project, recent chat) consumes these constants so
// changing the row height/radius/gap/font means one edit, not a hunt-and-peck
// across five components.
//
// Usage:
//   <div className={cn(sidebarRowBase, active ? sidebarRowActive : sidebarRowHover)}>
//     <Icon className={sidebarIconClass} />
//     <span className="flex-1 truncate">Label</span>
//     <ActionButton className={sidebarRowActionClass} />
//   </div>

/**
 * Corner radius of a row on a sidebar surface — the left rail's nav and chat rows, and the chat's
 * right-hand file panel. Shared so the two sides can't drift apart (they did: the file list was on the
 * theme's `rounded-md`, which is 12 px here, against the rail's 6 px). Note the theme's radius scale is
 * not monotonic — `--radius-md` (12 px) is LARGER than `--radius-lg` (10 px) — so the desktop value is
 * an explicit 6 px rather than a scale token, with the roomier `rounded-lg` for touch rows.
 */
export const sidebarRowRadius = 'rounded-[6px] max-md:rounded-lg';

/**
 * Container styling for any sidebar row — height, gap, radius, padding,
 * typography. Identical across desktop (≥ md) and mobile (`max-md:` variants
 * grow the row for touch). Does NOT include hover/active bg — combine with
 * `sidebarRowHover` or `sidebarRowActive` via `cn()`.
 *
 * The horizontal padding uses the SAME formula as `.sidebar-nav-link`
 * (collapsed rail): `calc(1.25rem - var(--sidebar-nav-icon) / 2)`. This
 * keeps the icon centre at exactly `wrapper px-3 + 1.25rem = 2rem` from the
 * sidebar's edge regardless of collapsed vs expanded state, so icons don't
 * jiggle horizontally during the open/close animation.
 */
export const sidebarRowBase =
  'group relative box-border flex h-8 max-md:h-11 cursor-pointer items-center ' +
  `gap-1.5 max-md:gap-3 ${sidebarRowRadius} ` +
  'px-[calc(1.25rem-var(--sidebar-nav-icon)/2)] py-0 ' +
  'sidebar-row-text leading-snug transition-colors text-sidebar-foreground';

/** Hover + active background — for non-current rows. */
export const sidebarRowHover =
  'hover:bg-sidebar-accent active:bg-sidebar-accent';

/** Active/current-page background — pinned highlight, no hover transition. */
export const sidebarRowActive = 'bg-sidebar-accent text-sidebar-foreground';

/** Leading icon + chevron sizing. Inherits text colour from row. */
export const sidebarIconClass =
  'size-[length:var(--sidebar-nav-icon)] shrink-0 text-inherit';

/**
 * Unread-message indicator. Small filled circle in brand-blue, sized to sit
 * comfortably next to the leading icon without inflating the row height.
 * Slack/Linear-style: presence of the dot IS the signal — no count, no badge.
 * The dot scales up slightly on touch breakpoints so it stays visible at
 * arm's-length thumb distance.
 *
 * Usage: `{conv.unread && <span className={sidebarUnreadDotClass} aria-label="Unread" />}`
 */
export const sidebarUnreadDotClass =
  'inline-block size-2 max-md:size-2.5 shrink-0 rounded-full bg-[#1850ff]';

/**
 * Stretches a row's primary action (the title button) over the whole highlighted row via an
 * invisible `::before` overlay, so every pixel the hover background covers — row padding and
 * flex gaps included — triggers the click. The row container must be `position: relative`
 * (part of `sidebarRowBase`); secondary controls inside the row must sit above the overlay
 * with `relative z-10` (part of `sidebarRowActionClass`) so they keep their own click handlers.
 */
export const sidebarRowStretchClass = 'before:absolute before:inset-0';

/**
 * Trailing action button (`+` on Projects header, options "⋯" on rows).
 * Always visible on mobile (≥ 44px tap target); hidden on desktop until
 * the parent row is hovered (`group-hover/<scope>:opacity-100`).
 *
 * Note: this class does NOT include the `group-hover` selector because the
 * scope name varies per row (e.g. `group-hover/projhead:` for the Projects
 * header, plain `group-hover:` for individual rows). Append the right
 * group-hover selector in the consumer.
 *
 * `sidebar-label` is included so the button is `display:none` when the
 * sidebar is collapsed (the rule lives in globals.css). `sidebar-fade` is
 * deliberately NOT included: its `sidebar-content-in` keyframe animation
 * settles on `opacity:1` and (because the animation uses `both`) keeps
 * that value past the animation, which overrides the Tailwind
 * `md:opacity-0` baseline — the action icon would then always be visible
 * after the sidebar expands, defeating the hover-to-reveal UX. The chat-
 * row equivalent in SidebarConversations.tsx also omits sidebar-fade for
 * the same reason; this shared primitive matches that behaviour.
 */
export const sidebarRowActionClass =
  'sidebar-label relative z-10 size-[1.625rem] max-md:size-11 shrink-0 ' +
  'text-sidebar-foreground hover:bg-foreground/10 active:bg-foreground/15 ' +
  'opacity-100 md:opacity-0';
